import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MovieCreationOutlined from "@mui/icons-material/MovieCreationOutlined";
import { OUTPUT_RESOLUTIONS, MIN_STITCH_CLIPS, MAX_STITCH_DURATION_SEC } from "./videoProjectConstants";
import StitchProgress from "./StitchProgress";
import { stitchVideoClips } from "../api/stitchVideo";
import { fetchVideoPricing } from "../api/fetchVideoPricing";
import { GlobalContext } from "../../../context/GlobalContext";

function uniqueAspectRatios(clips) {
  const set = new Set();
  for (const c of clips) {
    if (c.aspectRatio) set.add(c.aspectRatio);
  }
  return Array.from(set);
}

/** Fastest: stream-copy concat without re-scale. */
function defaultResolutionForClips() {
  return "source";
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   clips: import("./VideoProjectContext").ProjectClip[],
 *   totalDuration: number,
 *   lang?: object,
 * }} props
 */
export default function StitchModal({ open, onClose, clips, totalDuration, lang }) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [filename, setFilename] = React.useState("final_video.mp4");
  const [resolution, setResolution] = React.useState("source");
  const [aspectConfirmed, setAspectConfirmed] = React.useState(false);
  const [step, setStep] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [stitchCredits, setStitchCredits] = React.useState(null);

  React.useEffect(() => {
    if (!open || !hitAxios) return;
    fetchVideoPricing(hitAxios).then((p) => {
      const flat = Number(p?.stitching_flat) || 0;
      setStitchCredits(flat > 0 ? flat : null);
    });
  }, [open, hitAxios]);

  const aspectRatios = React.useMemo(() => uniqueAspectRatios(clips), [clips]);
  const needsAspectConfirm = aspectRatios.length > 1 && !aspectConfirmed;

  React.useEffect(() => {
    if (!open) return;
    setFilename("final_video.mp4");
    setResolution(defaultResolutionForClips());
    setAspectConfirmed(false);
    setStep(null);
    setResult(null);
    setError(null);
  }, [open, clips]);

  const handleStitch = async () => {
    if (clips.length < MIN_STITCH_CLIPS) return;
    if (totalDuration > MAX_STITCH_DURATION_SEC) {
      setError(
        lang?.videoProjectDurationCap ||
          `Total duration exceeds ${MAX_STITCH_DURATION_SEC} seconds.`,
      );
      return;
    }
    if (needsAspectConfirm) return;

    setError(null);
    setResult(null);
    setStep("stitching");

    try {
      const data = await stitchVideoClips(hitAxios, {
        clips: clips.map((c) => ({ url: c.url, duration: c.duration })),
        output_filename: filename,
        output_resolution: resolution,
      });
      setStep("done");
      setResult(data);
      if (data?.library_warning) {
        showSnack?.(data.library_warning, false);
      } else if (data?.job_id) {
        showSnack?.(
          lang?.videoProjectStitchDoneLibrary ||
            "Video stitched and added to your Video Library.",
          true,
        );
      } else {
        showSnack?.(lang?.videoProjectStitchDone || "Video stitched successfully.", true);
      }
    } catch (err) {
      setStep("error");
      setError(err?.message || "Stitching failed.");
    }
  };

  return (
    <Dialog open={open} onClose={step === "stitching" ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1 }}>
        <MovieCreationOutlined color="primary" />
        {lang?.videoStitchExportTitle || lang?.videoProjectStitchTitle || "Join videos — export"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {step ? (
            <StitchProgress step={step} lang={lang} elapsedSec={result?.elapsed_sec} />
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {lang?.videoProjectStitchPreviewHint ||
                  "Clips will be joined in this order (no transitions)."}
              </Typography>

              <Box
                sx={{
                  maxHeight: 160,
                  overflow: "auto",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  p: 1.5,
                }}
              >
                <Stack spacing={1}>
                  {clips.map((c, i) => (
                    <Stack key={c.id} direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" fontWeight={900} sx={{ minWidth: 20 }}>
                        {i + 1}.
                      </Typography>
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                        {c.modelLabel} · {c.duration}s
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              {needsAspectConfirm ? (
                <Alert severity="warning">
                  {(
                    lang?.videoProjectAspectWarning ||
                    "Clips have different aspect ratios ({ratios}). Stitching will not auto-crop — output may show letterboxing."
                  ).replace("{ratios}", aspectRatios.join(", "))}
                  <Button size="small" sx={{ mt: 1, fontWeight: 800 }} onClick={() => setAspectConfirmed(true)}>
                    {lang?.videoProjectAspectConfirm || "Continue anyway"}
                  </Button>
                </Alert>
              ) : null}

              <TextField
                size="small"
                label={lang?.videoProjectOutputFilename || "Output filename"}
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                fullWidth
              />

              <FormControl size="small" fullWidth>
                <InputLabel>{lang?.videoProjectOutputResolution || "Output resolution"}</InputLabel>
                <Select
                  label={lang?.videoProjectOutputResolution || "Output resolution"}
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                >
                  {OUTPUT_RESOLUTIONS.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {lang?.[r.labelKey] || r.fallback}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="caption" color="text.secondary" display="block">
                {lang?.videoStitchResolutionHint ||
                  "Fastest: “Match clips (no re-scale)”. 720p/1080p re-encodes the full video and takes longer."}
              </Typography>

              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {(lang?.videoProjectEstimatedOutput || "Estimated output") + `: ~${totalDuration}s`}
              </Typography>
              {stitchCredits != null ? (
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {(lang?.videoStitchCreditsCost || "Stitch cost")}: {stitchCredits}{" "}
                  {lang?.videoStudioCredits || "credits"}
                </Typography>
              ) : null}
            </>
          )}

          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : null}

          {result?.library_warning ? (
            <Alert severity="warning">{result.library_warning}</Alert>
          ) : null}

          {result?.output_url ? (
            <Stack spacing={1}>
              <Button
                variant="contained"
                fullWidth
                href={result.output_url}
                download={result.output_filename || "final_video.mp4"}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 800 }}
              >
                {lang?.videoProjectDownloadFinal || "Download final video"}
                {result.file_size_mb ? ` (${result.file_size_mb} MB)` : ""}
              </Button>
              {result.job_id ? (
                <Button
                  variant="outlined"
                  fullWidth
                  href={`/user?page=video-library&job=${result.job_id}`}
                  component="a"
                  sx={{ fontWeight: 800 }}
                >
                  {lang?.videoStitchViewInLibrary || "View in Video Library"}
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={step === "stitching"}>
          {result ? lang?.close || "Close" : lang?.cancel || "Cancel"}
        </Button>
        {!result ? (
          <Button
            variant="contained"
            onClick={handleStitch}
            disabled={
              clips.length < MIN_STITCH_CLIPS ||
              step === "stitching" ||
              needsAspectConfirm
            }
            sx={{ fontWeight: 800 }}
          >
            {lang?.videoProjectStartStitching || "Start Stitching"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
